import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, ExternalLink } from "lucide-react";
import { usePermissions } from "@/context/PermissionsContext";

const STATUS_COLORS = {
  planning: "bg-zinc-100 text-zinc-700",
  in_production: "bg-blue-100 text-blue-800",
  qa: "bg-amber-100 text-amber-800",
  delivered: "bg-emerald-100 text-emerald-800",
  on_hold: "bg-orange-100 text-orange-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function Projects() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canCreate = has("projects", "create");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", client_id: "", billing_model: "per_project", estimated_hours: 0, budget: 0, description: "" });

  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });

  const createMut = useMutation({
    mutationFn: (p) => api.post("/projects", p),
    onSuccess: () => {
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", client_id: "", billing_model: "per_project", estimated_hours: 0, budget: 0, description: "" });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.client_id || !form.name) {
      toast.error("Client and name are required");
      return;
    }
    createMut.mutate({
      ...form,
      estimated_hours: Number(form.estimated_hours) || 0,
      budget: Number(form.budget) || 0,
      description: form.description || undefined,
    });
  };

  return (
    <div data-testid="projects-page">
      <PageHeader
        eyebrow="Operations Division"
        title="Projects"
        description="Every approved quotation lands here. Track production, hours, revisions and delivery."
        actions={
          canCreate ? (
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button data-testid="new-project-btn"><Plus className="w-4 h-4 mr-1.5" /> New Project</Button></SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md">
              <SheetHeader><SheetTitle>Create a project</SheetTitle></SheetHeader>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="new-project-form">
                <div><Label>Client</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="prj-client"><SelectValue placeholder="Choose client" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label htmlFor="p-name">Project name</Label><Input id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="prj-name" /></div>
                <div><Label>Billing model</Label>
                  <Select value={form.billing_model} onValueChange={(v) => setForm({ ...form, billing_model: v })}>
                    <SelectTrigger data-testid="prj-billing"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="per_project">Per Project</SelectItem><SelectItem value="monthly_hours">Monthly Hours</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label htmlFor="p-hrs">Est. hours</Label><Input id="p-hrs" type="number" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} data-testid="prj-hours" /></div>
                  <div><Label htmlFor="p-bud">Budget</Label><Input id="p-bud" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="prj-budget" /></div>
                </div>
                <div><Label htmlFor="p-desc">Description</Label><Textarea id="p-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="prj-desc" /></div>
                <SheetFooter><Button type="submit" disabled={createMut.isPending} data-testid="prj-submit">Create Project</Button></SheetFooter>
              </form>
            </SheetContent>
          </Sheet>
          ) : null
        }
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="projects-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Billing</th>
                <th className="px-4 py-3 text-right">Budget</th><th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {projects.map((p) => (
                <tr key={p.id} data-testid={`prj-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{p.code}</td>
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-zinc-600">{p.client_name}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-widest ${STATUS_COLORS[p.status] || "bg-zinc-100 text-zinc-700"}`}>
                      {p.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 capitalize text-zinc-600">{p.billing_model.replace("_", " ")}</td>
                  <td className="px-4 py-3 text-right font-mono">{INR(p.budget)}</td>
                  <td className="px-4 py-3 text-right"><Link to={`/app/projects/${p.id}`} className="text-xs inline-flex items-center gap-1 hover:text-zinc-900" data-testid={`prj-open-${p.id}`}>Open <ExternalLink className="w-3 h-3" /></Link></td>
                </tr>
              ))}
              {projects.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-zinc-500">No projects yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
